import React, { useEffect, useState } from 'react';
import { toIdentifier } from 'src/shared/utils/toIdentifier';
import { AlertTriangle } from 'lucide-react';
import Input from 'src/shared/components/input/Input';
import { PrimaryButton, SecondaryButton } from 'src/shared/components/button/Button';
import FormCard from 'src/shared/components/form-card/FormCard';
import Checkbox from 'src/shared/components/checkbox/Checkbox';
import RadioGroup from 'src/shared/components/radio-group/RadioGroup';
import Select from 'src/shared/components/select/Select';
import BasicDatePicker from 'src/shared/components/basic-datepicker/BasicDatePicker';
import { toCalendarDate, toDayString } from 'src/shared/components/basic-datepicker/calendarDay';
import type { SchemaViolation } from 'src/shared/state/api-slices/engineErrorMessage';
import { collectionRefusals } from '../../domain/documents/refusals';
import {
    LICENCE_OPTIONS,
    validateCollectionForm,
    type CollectionFormErrors,
    type CollectionFormValues,
    type SpatialExtentChoice,
    type TemporalEndChoice,
} from '../../domain/documents/stacCollectionDocument';

type CollectionFormProps = {
    mode: 'create' | 'edit';
    initialValues: CollectionFormValues;
    isSaving: boolean;
    /** What the engine refused, shown as it was said rather than flattened to "failed". */
    submitError?: string;
    /** Which rules the engine says the document broke, and where. */
    submitViolations?: SchemaViolation[];
    onSubmit: (values: CollectionFormValues) => void;
    /** Reports what the form holds, so the JSON hatch can open on it rather than on what was loaded. */
    onValuesChange?: (values: CollectionFormValues) => void;
    onCancel: () => void;
};

const fieldError = (message: string | undefined, testId?: string) =>
    message && (
        <p className='text-xs text-red-100' data-testid={testId}>
            {message}
        </p>
    );

const hint = (message: string) => <p className='text-xs text-grey-70'>{message}</p>;

const CollectionForm = ({
    mode,
    initialValues,
    isSaving,
    submitError,
    submitViolations = [],
    onSubmit,
    onValuesChange,
    onCancel,
}: CollectionFormProps) => {
    const [values, setValues] = useState(initialValues);
    const [errors, setErrors] = useState<CollectionFormErrors>({});

    // Reported from one place rather than from each handler: the footprint radios, the role
    // pips and the asset editor all change `values` without going through the same path, and
    // any of them missed would open the JSON hatch on a stale document.
    useEffect(() => onValuesChange?.(values), [values, onValuesChange]);
    /**
     * Fields the engine's refusal named. Kept apart from `errors`, which are ours: these come
     * back after a round trip, and clearing them follows editing rather than revalidation.
     */
    /** The rules the engine says this document broke, less any whose field has since been edited. */
    const [refused, setRefused] = useState<SchemaViolation[]>([]);
    /**
     * Whether the super user has written an identifier of their own. Until they do, the
     * identifier follows the title — checking only that the field is non-empty would stop
     * after the title's first letter, leaving `s` for "Species Risk Predictions".
     */
    const [isIdentifierOwn, setIsIdentifierOwn] = useState(mode === 'edit');

    const change = (field: keyof CollectionFormValues, value: unknown) => {
        setValues(previous => ({ ...previous, [field]: value }));
        // Clearing on edit rather than revalidating: a half-typed field is not yet wrong.
        setErrors(previous => ({ ...previous, [field]: undefined }));
        // This form's inputs are the document's own top-level fields, so a refusal belongs to
        // one when its path is that field alone.
        setRefused(previous => previous.filter(violation => violation.path[0] !== field));
    };

    const changeTitle = (title: string) => {
        setValues(previous => ({
            ...previous,
            title,
            id: isIdentifierOwn ? previous.id : toIdentifier(title),
        }));
        setErrors(previous => ({ ...previous, title: undefined, id: undefined }));
    };

    const changeIdentifier = (id: string) => {
        setIsIdentifierOwn(true);
        change('id', id);
    };

    // A new refusal replaces the last one; the same one does not re-mark a field the super
    // user has since edited.
    const [lastRefusal, setLastRefusal] = useState<string | undefined>();
    if (submitError !== lastRefusal) {
        setLastRefusal(submitError);
        setRefused(submitViolations);
    }

    /**
     * What to show against each input: what this form found wrong, and what the engine did.
     *
     * The engine's refusals are put into the same shape validation produces, so every field
     * shows one the way it already shows its own — no input has to be wired up a second time,
     * and none is left silently unable to report a refusal.
     *
     * Validation wins where both speak: it runs on what is on screen now, where a refusal is
     * about the document as it was sent.
     */
    const shown: CollectionFormErrors = { ...collectionRefusals(refused), ...errors };

    /**
     * A UTC day, held as `YYYY-MM-DD`.
     *
     * The picker works in `Date`s and formats them locally, so the day crosses the boundary as
     * local midnight — a UTC midnight would show as the day before west of Greenwich.
     */
    const dayField = (name: 'temporalStart' | 'temporalEndDate', label: string, testId: string) => (
        <BasicDatePicker
            id={testId}
            testId={testId}
            label={label}
            // The same label as every other field in this form wears.
            labelClassName='text-sm font-medium text-primary'
            value={toCalendarDate(values[name])}
            disabled={isSaving}
            hasError={Boolean(shown[name])}
            onChange={chosen => change(name, toDayString(chosen))}
        />
    );

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        const found = validateCollectionForm(values);
        setErrors(found);
        if (Object.keys(found).length === 0) {
            onSubmit(values);
        }
    };

    /**
     * A regional extent cannot be stated here, so the form says so rather than letting a save
     * fail. A dataset that *already* declares one is exempt: a pipeline can catalogue one
     * through the API, and blocking those would leave them permanently uneditable.
     */
    const isBlockedByExtent = values.spatialExtent === 'regional' && !values.hasStoredRegionalExtent;

    // An edit must be able to save a licence we no longer offer without silently changing it.
    const licenceOptions = LICENCE_OPTIONS.some(option => option.value === values.license)
        ? LICENCE_OPTIONS
        : [{ value: values.license, label: `${values.license} (as catalogued)` }, ...LICENCE_OPTIONS];

    return (
        <form className='flex w-full max-w-3xl flex-col gap-6' onSubmit={handleSubmit} noValidate>
            <FormCard title='Identity' testId='identity'>
                <div className='flex flex-col gap-1'>
                    <label htmlFor='collection-title' className='text-sm font-medium text-primary'>
                        Title
                    </label>
                    <Input
                        id='collection-title'
                        data-testid='collection-title'
                        aria-invalid={Boolean(shown.title) || undefined}
                        value={values.title}
                        placeholder='e.g. Species Risk Predictions'
                        onChange={event => changeTitle(event.target.value)}
                    />
                    {fieldError(shown.title, 'collection-title-error')}
                </div>

                <div className='flex flex-col gap-1'>
                    <label htmlFor='collection-id' className='text-sm font-medium text-primary'>
                        Identifier
                    </label>
                    <Input
                        id='collection-id'
                        data-testid='collection-id'
                        value={values.id}
                        placeholder='e.g. species-risk-predictions'
                        // The path names the dataset and the body must agree, so an edit cannot rename.
                        disabled={mode === 'edit'}
                        onChange={event => changeIdentifier(event.target.value)}
                    />
                    {!shown.id &&
                        hint(
                            mode === 'edit'
                                ? 'A dataset cannot be renamed.'
                                : 'Proposed from the title, and used in every URL that addresses this dataset.'
                        )}
                    {fieldError(shown.id, 'collection-id-error')}
                </div>

                <div className='flex flex-col gap-1'>
                    <label htmlFor='collection-description' className='text-sm font-medium text-primary'>
                        Description
                    </label>
                    <Input
                        id='collection-description'
                        data-testid='collection-description'
                        aria-invalid={Boolean(shown.description) || undefined}
                        value={values.description}
                        onChange={event => change('description', event.target.value)}
                    />
                    {fieldError(shown.description, 'collection-description-error')}
                </div>
            </FormCard>

            <FormCard title='Licence' testId='licence'>
                <div className='flex flex-col gap-1'>
                    <Select
                        label='Licence'
                        testId='collection-license'
                        width='100%'
                        selectedValue={values.license}
                        isInvalid={Boolean(shown.license)}
                        onChange={chosen => change('license', chosen)}
                        options={licenceOptions.map(({ value, label }) => ({ value, label }))}
                    />
                    {fieldError(shown.license, 'collection-license-error')}
                </div>

                <div className='flex flex-col gap-1'>
                    <Checkbox
                        checked={values.producedByNatureMetrics}
                        onChange={checked => change('producedByNatureMetrics', checked)}
                        label='Produced by NatureMetrics'
                    />
                    {hint('Records NatureMetrics as the provider and licensor.')}
                </div>

                {/* Beside the licence because it answers the other half of the same question:
                    the licence says what may be done with the data, this who must be credited
                    for doing it. Written as a STAC provider with the `licensor` role, which is
                    where STAC keeps credit — a field of our own would be invisible to every
                    other client that reads this catalogue. */}
                <div className='flex flex-col gap-1'>
                    <label
                        htmlFor='collection-attributed-to'
                        className='text-sm font-medium text-primary'
                        data-testid='collection-attributed-to-label'
                    >
                        Attributed to
                        <span className='ml-1 text-xs font-normal text-muted-foreground'>(optional)</span>
                    </label>
                    <Input
                        id='collection-attributed-to'
                        data-testid='collection-attributed-to'
                        aria-invalid={Boolean(shown.attributedTo) || undefined}
                        value={values.attributedTo}
                        placeholder='e.g. Natural England'
                        onChange={event => change('attributedTo', event.target.value)}
                    />
                    {!shown.attributedTo && hint('Who licenses this data and must be credited. Left blank where nobody need be.')}
                    {fieldError(shown.attributedTo, 'collection-attributed-to-error')}
                </div>

                {values.attributedTo.trim() !== '' && (
                    <>
                        <div className='flex flex-col gap-1'>
                            <label htmlFor='collection-attribution-credit' className='text-sm font-medium text-primary'>
                                Credit wording
                                <span className='ml-1 text-xs font-normal text-muted-foreground'>(optional)</span>
                            </label>
                            <Input
                                id='collection-attribution-credit'
                                data-testid='collection-attribution-credit'
                                value={values.attributionCredit}
                                placeholder='e.g. © Natural England 2026, licensed under OGL v3.0'
                                onChange={event => change('attributionCredit', event.target.value)}
                            />
                            {hint('The form of words they ask to be credited with, if they ask for one.')}
                        </div>

                        <div className='flex flex-col gap-1'>
                            <label htmlFor='collection-attribution-url' className='text-sm font-medium text-primary'>
                                Homepage
                                <span className='ml-1 text-xs font-normal text-muted-foreground'>(optional)</span>
                            </label>
                            <Input
                                id='collection-attribution-url'
                                data-testid='collection-attribution-url'
                                value={values.attributionUrl}
                                placeholder='https://'
                                onChange={event => change('attributionUrl', event.target.value)}
                            />
                        </div>
                    </>
                )}
            </FormCard>

            <FormCard title='Spatial extent' testId='spatial-extent'>
                <RadioGroup
                    options={[
                        { value: 'global', label: 'Global — the dataset covers the whole world' },
                        { value: 'regional', label: 'Regional — the dataset covers part of it' },
                    ]}
                    selectedOption={values.spatialExtent}
                    onChange={chosen => change('spatialExtent', chosen as SpatialExtentChoice)}
                />
                {values.spatialExtent === 'regional' && values.hasStoredRegionalExtent && (
                    <p className='text-xs text-grey-70' data-testid='collection-extent-stored'>
                        This dataset already declares a narrower extent ({values.bbox.join(', ')}). It is kept exactly as catalogued —
                        editing the extent here is not supported yet.
                    </p>
                )}
            </FormCard>

            <FormCard title='Temporal extent' testId='temporal-extent'>
                <div className='flex flex-col gap-1 max-w-xs'>
                    {dayField('temporalStart', 'From', 'collection-temporal-start')}
                    {fieldError(shown.temporalStart, 'collection-temporal-start-error')}
                </div>

                <RadioGroup
                    options={[
                        { value: 'ongoing', label: 'Ongoing — versions are still being added' },
                        { value: 'ended', label: 'Ended — the dataset stops at a known date' },
                    ]}
                    selectedOption={values.temporalEnd}
                    onChange={chosen => change('temporalEnd', chosen as TemporalEndChoice)}
                />

                {values.temporalEnd === 'ended' && (
                    <div className='flex flex-col gap-1 max-w-xs'>
                        {dayField('temporalEndDate', 'Last day covered', 'collection-temporal-end-date')}
                        {fieldError(shown.temporalEndDate, 'collection-temporal-end-date-error')}
                    </div>
                )}
            </FormCard>

            {submitError && (
                <p className='text-sm text-red-100' data-testid='collection-form-error'>
                    {submitError}
                </p>
            )}

            {/* The callout sits under the buttons, not beside them, so they do not shift
                position when it appears. */}
            <div className='flex flex-col gap-3' data-testid='collection-form-actions'>
                <div className='flex gap-3'>
                    <PrimaryButton type='submit' disabled={isSaving || isBlockedByExtent} data-testid='collection-form-submit'>
                        {mode === 'create' ? 'Create dataset' : 'Save changes'}
                    </PrimaryButton>
                    <SecondaryButton type='button' onClick={onCancel} disabled={isSaving} data-testid='collection-form-cancel'>
                        Cancel
                    </SecondaryButton>
                </div>

                {isBlockedByExtent && (
                    <div
                        className='flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 max-w-xl'
                        data-testid='collection-extent-unsupported'
                        role='status'
                    >
                        <AlertTriangle className='w-4 h-4 mt-0.5 shrink-0 text-amber-600' />
                        <p className='text-xs text-primary'>
                            <span className='font-semibold'>Regional extents are not supported yet.</span> Drawing one needs a map this form
                            does not have. Set the extent back to global to save this dataset.
                        </p>
                    </div>
                )}
            </div>
        </form>
    );
};

export default CollectionForm;
